import * as THREE from 'three';

const VERT = /* glsl */ `
attribute vec3 iPos;
attribute vec4 iCol;
attribute vec2 iFill; // x = fill ratio, y = width
varying vec2 vUv;
varying vec4 vCol;
varying float vFill;
void main() {
  vUv = uv;
  vCol = iCol;
  vFill = iFill.x;
  vec4 mv = modelViewMatrix * vec4(iPos, 1.0);
  mv.xy += vec2(position.x * iFill.y, position.y * 0.075);
  gl_Position = projectionMatrix * mv;
}`;

const FRAG = /* glsl */ `
varying vec2 vUv;
varying vec4 vCol;
varying float vFill;
void main() {
  vec2 e = min(vUv, 1.0 - vUv);
  if (e.y < 0.18 || e.x < 0.012) { gl_FragColor = vec4(0.05, 0.05, 0.06, 0.9); return; }
  gl_FragColor = vUv.x <= vFill ? vec4(vCol.rgb, 1.0) : vec4(0.13, 0.13, 0.15, 0.85);
  #include <colorspace_fragment>
}`;

/** Health bars above units: one instanced billboard draw call. */
export class BarLayer {
  readonly mesh: THREE.Mesh;
  private geo = new THREE.InstancedBufferGeometry();
  private pos: Float32Array;
  private col: Float32Array;
  private fill: Float32Array;
  private n = 0;

  constructor(private cap = 512) {
    const quad = new THREE.PlaneGeometry(1, 1);
    this.geo.index = quad.index;
    this.geo.setAttribute('position', quad.getAttribute('position'));
    this.geo.setAttribute('uv', quad.getAttribute('uv'));
    this.pos = new Float32Array(cap * 3);
    this.col = new Float32Array(cap * 4);
    this.fill = new Float32Array(cap * 2);
    this.geo.setAttribute('iPos', new THREE.InstancedBufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('iCol', new THREE.InstancedBufferAttribute(this.col, 4).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('iFill', new THREE.InstancedBufferAttribute(this.fill, 2).setUsage(THREE.DynamicDrawUsage));
    const mat = new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, transparent: true, depthWrite: false, depthTest: false });
    this.mesh = new THREE.Mesh(this.geo, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 20;
  }

  begin(): void {
    this.n = 0;
  }

  add(p: THREE.Vector3, ratio: number, color: THREE.Color, width = 0.5): void {
    if (this.n >= this.cap) return;
    const i = this.n++;
    this.pos.set([p.x, p.y, p.z], i * 3);
    this.col.set([color.r, color.g, color.b, 1], i * 4);
    this.fill.set([Math.max(0, Math.min(1, ratio)), width], i * 2);
  }

  get count(): number {
    return this.n;
  }

  /** The bars added since `from`, to paste() again on a later fill. */
  copyFrom(from: number): Float32Array[] {
    return [this.pos.slice(from * 3, this.n * 3), this.col.slice(from * 4, this.n * 4), this.fill.slice(from * 2, this.n * 2)];
  }

  /** Adds bars copied with copyFrom(), as if added one by one (what doesn't fit is left out). */
  paste([pos, col, fill]: Float32Array[]): void {
    const k = Math.min((pos as Float32Array).length / 3, this.cap - this.n);
    if (k <= 0) return;
    this.pos.set((pos as Float32Array).subarray(0, k * 3), this.n * 3);
    this.col.set((col as Float32Array).subarray(0, k * 4), this.n * 4);
    this.fill.set((fill as Float32Array).subarray(0, k * 2), this.n * 2);
    this.n += k;
  }

  end(): void {
    this.geo.instanceCount = this.n;
    for (const k of ['iPos', 'iCol', 'iFill']) (this.geo.getAttribute(k) as THREE.InstancedBufferAttribute).needsUpdate = true;
    this.mesh.visible = this.n > 0;
  }
}
